import React, { useContext, useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { LoadingCircle } from '../../UI/Custom/LoadingCircle';
import { getAffiliatorLeadFailures } from '../../../utils/api/Affiliate/getAffiliatorLeadFailuresApi';
import styles from '../../../styles/components/Modal/Affiliate/AffiliatorLeadFailuresModal.module.css';

const translations = {
    ru: {
        title: 'Ошибки лидов',
        subtitle: 'Заявки от аффилиатов, которые CRM или платформа не смогли принять',
        total: 'Всего ошибок',
        date: 'Дата и время',
        affiliate: 'Аффилиат',
        lead: 'Данные лида',
        error: 'Результат',
        details: 'Детали',
        unknownAffiliate: 'Не определён',
        empty: 'Ошибок лидов пока нет',
        emptyHint: 'Новые отклонённые заявки появятся здесь автоматически.',
        loadingError: 'Не удалось загрузить ошибки лидов.',
        retry: 'Повторить',
        previous: 'Назад',
        next: 'Далее',
        page: 'Страница',
        of: 'из',
        upstream: 'Статус платформы',
        requestId: 'Request ID',
        rejected: 'Отклонён',
        failed: 'Ошибка сервера',
        showDetails: 'Показать детали',
        hideDetails: 'Скрыть детали',
        leadPayload: 'Отправленные данные',
        errorDetails: 'Описание ошибки',
        technicalDetails: 'Технические данные',
        noPayload: 'Данные лида не были переданы.',
        noErrorDetails: 'Дополнительное описание отсутствует.',
        copied: 'Скопировано',
        copyRequestId: 'Скопировать Request ID',
        affiliateId: 'ID аффилиата',
        status: 'HTTP статус',
        stage: 'Этап',
        errorCode: 'Код ошибки',
        method: 'Метод',
        path: 'Endpoint',
        ip: 'IP адрес',
        duration: 'Время обработки',
        exception: 'Exception',
        userAgent: 'User agent',
        payloadLabels: {
            first_name: 'Имя',
            last_name: 'Фамилия',
            email: 'Email',
            phone: 'Телефон',
            second_phone: 'Доп. телефон',
            campaign: 'Кампания',
            extra_info: 'Доп. информация',
            geo: 'Гео',
        },
    },
    en: {
        title: 'Lead Failures',
        subtitle: 'Affiliate submissions that the CRM or platform could not accept',
        total: 'Total failures',
        date: 'Date and time',
        affiliate: 'Affiliate',
        lead: 'Lead data',
        error: 'Result',
        details: 'Details',
        unknownAffiliate: 'Unmatched',
        empty: 'No lead failures yet',
        emptyHint: 'New rejected submissions will appear here automatically.',
        loadingError: 'Failed to load lead failures.',
        retry: 'Retry',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        of: 'of',
        upstream: 'Platform status',
        requestId: 'Request ID',
        rejected: 'Rejected',
        failed: 'Server error',
        showDetails: 'Show details',
        hideDetails: 'Hide details',
        leadPayload: 'Submitted data',
        errorDetails: 'Error details',
        technicalDetails: 'Technical details',
        noPayload: 'No lead data was submitted.',
        noErrorDetails: 'No additional error details available.',
        copied: 'Copied',
        copyRequestId: 'Copy Request ID',
        affiliateId: 'Affiliate ID',
        status: 'HTTP status',
        stage: 'Stage',
        errorCode: 'Error code',
        method: 'Method',
        path: 'Endpoint',
        ip: 'IP address',
        duration: 'Duration',
        exception: 'Exception',
        userAgent: 'User agent',
        payloadLabels: {
            first_name: 'First name',
            last_name: 'Last name',
            email: 'Email',
            phone: 'Phone',
            second_phone: 'Second phone',
            campaign: 'Campaign',
            extra_info: 'Extra info',
            geo: 'Geo',
        },
    },
};

const PER_PAGE = 15;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const displayValue = (value) => {
    if (Array.isArray(value)) {
        return value.map(displayValue).join(', ');
    }

    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    return hasValue(value) ? String(value) : '—';
};

const ErrorContext = ({ value }) => {
    if (!hasValue(value)) {
        return null;
    }

    if (Array.isArray(value)) {
        return (
            <ul className={styles.contextList}>
                {value.map((item, index) => <li key={index}><ErrorContext value={item} /></li>)}
            </ul>
        );
    }

    if (typeof value === 'object') {
        return (
            <div className={styles.contextRows}>
                {Object.entries(value).map(([key, item]) => (
                    <div className={styles.contextRow} key={key}>
                        <span>{key.replaceAll('_', ' ')}</span>
                        <ErrorContext value={item} />
                    </div>
                ))}
            </div>
        );
    }

    return <span>{displayValue(value)}</span>;
};

const DetailItem = ({ label, value, wide = false, children }) => (
    <div className={`${styles.detailItem} ${wide ? styles.wideDetailItem : ''}`}>
        <span className={styles.detailLabel}>{label}</span>
        <div className={styles.detailValue}>{children || displayValue(value)}</div>
    </div>
);

const FailureDetails = ({ failure, t, copiedRequestId, onCopyRequestId }) => {
    const payloadEntries = Object.entries(failure.payload || {}).filter(([, value]) => hasValue(value));
    const technicalItems = [
        [t.affiliateId, failure.affiliator_id],
        [t.status, `HTTP ${failure.http_status}`],
        [t.stage, failure.stage],
        [t.errorCode, failure.error_code],
        [t.upstream, failure.upstream_status ? `HTTP ${failure.upstream_status}` : null],
        [t.method, failure.method],
        [t.path, failure.path],
        [t.ip, failure.ip],
        [t.duration, Number.isFinite(failure.duration_ms) ? `${failure.duration_ms} ms` : null],
        [t.exception, failure.exception_class],
        [t.userAgent, failure.user_agent],
    ].filter(([, value]) => hasValue(value));

    return (
        <div className={styles.expandedContent}>
            <section className={styles.detailSection}>
                <h3>{t.leadPayload}</h3>
                {payloadEntries.length > 0 ? (
                    <div className={styles.detailsGrid}>
                        {payloadEntries.map(([key, value]) => (
                            <DetailItem
                                key={key}
                                label={t.payloadLabels[key] || key.replaceAll('_', ' ')}
                                value={value}
                                wide={key === 'extra_info'}
                            />
                        ))}
                    </div>
                ) : <p className={styles.mutedText}>{t.noPayload}</p>}
            </section>

            <section className={styles.detailSection}>
                <h3>{t.errorDetails}</h3>
                {hasValue(failure.error_message) && (
                    <div className={styles.errorMessage}>{failure.error_message}</div>
                )}
                {failure.error_context && Object.keys(failure.error_context).length > 0 ? (
                    <ErrorContext value={failure.error_context} />
                ) : !hasValue(failure.error_message) && (
                    <p className={styles.mutedText}>{t.noErrorDetails}</p>
                )}
            </section>

            <section className={`${styles.detailSection} ${styles.technicalSection}`}>
                <h3>{t.technicalDetails}</h3>
                <div className={styles.detailsGrid}>
                    <DetailItem label={t.requestId} wide>
                        <div className={styles.copyValue}>
                            <code>{failure.request_id || '—'}</code>
                            {failure.request_id && (
                                <button
                                    type="button"
                                    className={styles.copyButton}
                                    onClick={() => onCopyRequestId(failure.request_id)}
                                    title={t.copyRequestId}
                                    aria-label={t.copyRequestId}
                                >
                                    {copiedRequestId === failure.request_id ? <CheckIcon /> : <ContentCopyIcon />}
                                    <span>{copiedRequestId === failure.request_id ? t.copied : t.copyRequestId}</span>
                                </button>
                            )}
                        </div>
                    </DetailItem>
                    {technicalItems.map(([label, value]) => (
                        <DetailItem
                            key={label}
                            label={label}
                            value={value}
                            wide={label === t.path || label === t.exception || label === t.userAgent}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
};

export const AffiliatorLeadFailuresModal = ({ onClose }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';
    const copyTimerRef = useRef(null);

    const [failures, setFailures] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [expandedFailureId, setExpandedFailureId] = useState(null);
    const [copiedRequestId, setCopiedRequestId] = useState('');

    useEffect(() => {
        const controller = new AbortController();

        const loadFailures = async () => {
            setIsLoading(true);
            setError('');

            try {
                const response = await getAffiliatorLeadFailures(page, PER_PAGE, controller.signal);
                setFailures(response.data);
                setLastPage(response.meta.last_page);
                setTotal(response.meta.total);
                setExpandedFailureId((currentId) => (
                    response.data.some((failure) => failure.id === currentId) ? currentId : null
                ));
            } catch (requestError) {
                if (requestError?.name !== 'AbortError') {
                    setFailures([]);
                    setError(requestError?.message || t.loadingError);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        loadFailures();

        return () => controller.abort();
    }, [page, reloadKey, t.loadingError]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
            window.clearTimeout(copyTimerRef.current);
        };
    }, [onClose]);

    const getAffiliateLabel = (failure) => {
        if (!failure.affiliator_id) {
            return t.unknownAffiliate;
        }

        return failure.affiliator_name || failure.affiliate || `#${failure.affiliator_id}`;
    };

    const getLeadName = (payload = {}) => {
        const name = [payload.first_name, payload.last_name].filter(Boolean).join(' ').trim();
        return name || '—';
    };

    const copyRequestId = async (requestId) => {
        if (!navigator.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText(requestId);
            setCopiedRequestId(requestId);
            window.clearTimeout(copyTimerRef.current);
            copyTimerRef.current = window.setTimeout(() => setCopiedRequestId(''), 2000);
        } catch {
            setCopiedRequestId('');
        }
    };

    return (
        <div className={styles.modalOverlay} onMouseDown={onClose}>
            <div
                className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="affiliate-lead-failures-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <div className={styles.titleGroup}>
                        <div className={styles.titleIcon}><ErrorOutlineIcon /></div>
                        <div>
                            <h2 id="affiliate-lead-failures-title">{t.title}</h2>
                            <p>{t.subtitle}</p>
                        </div>
                    </div>
                    <div className={styles.headerActions}>
                        <div className={styles.totalBadge}>
                            <span>{t.total}</span>
                            <strong>{total}</strong>
                        </div>
                        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                            <CloseIcon />
                        </button>
                    </div>
                </div>

                <div className={styles.modalBody}>
                    {isLoading ? (
                        <div className={styles.stateBlock}>
                            <LoadingCircle size={40} thickness={3} color="#6c63ff" />
                        </div>
                    ) : error ? (
                        <div className={styles.stateBlock}>
                            <ErrorOutlineIcon className={styles.stateIcon} />
                            <p className={styles.errorText}>{error}</p>
                            <button className={styles.retryButton} onClick={() => setReloadKey((value) => value + 1)}>
                                <RefreshIcon />
                                {t.retry}
                            </button>
                        </div>
                    ) : failures.length === 0 ? (
                        <div className={styles.stateBlock}>
                            <ErrorOutlineIcon className={styles.emptyIcon} />
                            <strong>{t.empty}</strong>
                            <span>{t.emptyHint}</span>
                        </div>
                    ) : (
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t.date}</th>
                                        <th>{t.affiliate}</th>
                                        <th>{t.lead}</th>
                                        <th>{t.error}</th>
                                        <th className={styles.actionHeader}>{t.details}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failures.map((failure) => {
                                        const isExpanded = expandedFailureId === failure.id;
                                        const [date, time] = String(failure.created_at || '').split(' ');
                                        const detailsId = `affiliate-failure-details-${failure.id}`;

                                        return (
                                            <React.Fragment key={failure.id}>
                                                <tr className={isExpanded ? styles.expandedRow : ''}>
                                                    <td className={styles.dateCell}>
                                                        <strong>{date || '—'}</strong>
                                                        <span className={styles.secondaryText}>{time || ''}</span>
                                                    </td>
                                                    <td>
                                                        <strong>{getAffiliateLabel(failure)}</strong>
                                                        {failure.affiliate && failure.affiliator_name && (
                                                            <span className={styles.secondaryText}>{failure.affiliate}</span>
                                                        )}
                                                        {failure.affiliator_id && (
                                                            <span className={styles.secondaryText}>ID: {failure.affiliator_id}</span>
                                                        )}
                                                    </td>
                                                    <td className={styles.leadCell}>
                                                        <strong>{getLeadName(failure.payload)}</strong>
                                                        <span className={styles.secondaryText}>{failure.payload?.email || '—'}</span>
                                                        {failure.payload?.phone && (
                                                            <span className={styles.secondaryText}>{failure.payload.phone}</span>
                                                        )}
                                                    </td>
                                                    <td className={styles.resultCell}>
                                                        <div className={styles.resultLine}>
                                                            <span className={`${styles.statusBadge} ${failure.outcome === 'failed' ? styles.failedStatus : styles.rejectedStatus}`}>
                                                                HTTP {failure.http_status}
                                                            </span>
                                                            <span className={styles.outcomeText}>
                                                                {failure.outcome === 'failed' ? t.failed : t.rejected}
                                                            </span>
                                                        </div>
                                                        <strong className={styles.errorCode}>{failure.error_code || '—'}</strong>
                                                        {failure.error_message && (
                                                            <span className={styles.errorPreview}>{failure.error_message}</span>
                                                        )}
                                                    </td>
                                                    <td className={styles.actionCell}>
                                                        <button
                                                            type="button"
                                                            className={`${styles.detailsButton} ${isExpanded ? styles.activeDetailsButton : ''}`}
                                                            onClick={() => setExpandedFailureId(isExpanded ? null : failure.id)}
                                                            aria-expanded={isExpanded}
                                                            aria-controls={detailsId}
                                                        >
                                                            <span>{isExpanded ? t.hideDetails : t.showDetails}</span>
                                                            <KeyboardArrowDownIcon />
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className={styles.detailsRow}>
                                                        <td colSpan="5" id={detailsId}>
                                                            <FailureDetails
                                                                failure={failure}
                                                                t={t}
                                                                copiedRequestId={copiedRequestId}
                                                                onCopyRequestId={copyRequestId}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!isLoading && !error && lastPage > 1 && (
                    <div className={styles.pagination}>
                        <div className={styles.paginationControls}>
                            <button onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1}>
                                {t.previous}
                            </button>
                            <span>{t.page} <strong>{page}</strong> {t.of} {lastPage}</span>
                            <button onClick={() => setPage((value) => Math.min(lastPage, value + 1))} disabled={page === lastPage}>
                                {t.next}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
